import { createBrowserRouter, Navigate, isRouteErrorResponse, useRouteError } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import App from "./App";
import { ProtectedLayout } from "./layouts/protected-layout";
import { PlatformAdminProtectedLayout } from "./layouts/platform-admin-protected-layout";

// Loading component
const PageLoader = ({ transparent }: { transparent?: boolean } = {}) => (
  <div className={`flex min-h-screen items-center justify-center ${transparent ? "" : "bg-brand-25"}`}>
    <div className={`h-12 w-12 animate-spin rounded-full border-4 ${transparent ? "border-gray-200 border-t-gray-400" : "border-brand-200 border-t-brand-500"}`} />
  </div>
);

// 部署後舊分頁還留著上一版的 index.html，裡面寫死的是舊版帶 hash 的 chunk 檔名；
// 使用者在舊分頁點進某個 lazy route 時，瀏覽器抓不到那個舊檔名（伺服器已經是新版本），
// import() 失敗會被 React Router 的預設錯誤頁攔下來，直接顯示英文的
// "Unexpected Application Error!"。這裡偵測出是這種「chunk 抓不到」的錯誤時自動重新整理一次，
// 讓使用者拿到最新版頁面，不用自己手動重整；如果重整一次還是失敗（真的斷線/伺服器問題），
// 10 秒內不會再自動重整，改顯示可手動重試的中文錯誤畫面，避免無限重整迴圈。
const isChunkLoadError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  return /fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|loading chunk .* failed/i.test(message);
};

const CHUNK_RELOAD_KEY = "aq1_chunk_reload_at";

const RootErrorBoundary = () => {
  const error = useRouteError();
  const chunkLoadError = isChunkLoadError(error);
  // 只有「這次是第一次遇到」才會觸發自動重整並顯示 loading 轉圈；如果 10 秒內已經重整過一次
  // 還是同樣的錯誤（代表重整也救不回來，例如伺服器/CDN 快取問題，不是單純客戶端快取），
  // 就不能再無限顯示轉圈（那樣使用者看到的就是「整頁空白」），要落到下面有文字有按鈕的畫面。
  const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
  const alreadyTriedReload = Date.now() - lastReloadAt < 10000;

  useEffect(() => {
    if (!chunkLoadError || alreadyTriedReload) return;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    window.location.reload();
  }, [chunkLoadError, alreadyTriedReload]);

  if (chunkLoadError && !alreadyTriedReload) {
    return <PageLoader />;
  }

  const message = chunkLoadError
    ? "頁面版本已更新，請重新整理後再試一次。"
    : isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
    ? error.message
    : "發生未預期的錯誤";

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-brand-25 px-6 text-center">
      <p className="text-lg font-bold text-gray-800">頁面載入失敗</p>
      <p className="max-w-sm text-sm text-gray-500">{message}</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-full bg-brand-500 px-6 py-2 text-sm font-semibold text-white transition hover:bg-brand-600"
      >
        重新整理
      </button>
    </div>
  );
};

// Lazy load pages for better performance
// Auth pages
const LoginPage = lazy(() => import("./pages/auth/login"));
const RegisterPage = lazy(() => import("./pages/auth/register"));
const ForgotPasswordPage = lazy(() => import("./pages/auth/forgot-password"));
const ResetPasswordPage = lazy(() => import("./pages/auth/reset-password"));

// Protected pages
const DashboardPage = lazy(() => import("./pages/dashboard"));
const BookingsPage = lazy(() => import("./pages/bookings"));
const BookingNewPage = lazy(() => import("./pages/bookings/new"));
const BookingBatchPage = lazy(() => import("./pages/bookings/batch"));
const BookingDetailPage = lazy(() => import("./pages/bookings/[bookingId]"));
const PersonalEventEditPage = lazy(() => import("./pages/bookings/personal-event-edit"));
const CustomersPage = lazy(() => import("./pages/customers"));
const CustomerNewPage = lazy(() => import("./pages/customers/new"));
const CustomerDetailPage = lazy(() => import("./pages/customers/[customerId]"));
const CustomerPackagesPage = lazy(() => import("./pages/customers/[customerId]/packages"));
const CustomerBookingsPage = lazy(() => import("./pages/customers/[customerId]/bookings"));
const CustomerRatingsPage = lazy(() => import("./pages/customers/[customerId]/ratings"));
const CustomerCouponHistoryPage = lazy(() => import("./pages/customers/[customerId]/coupon-history"));
const CustomerLoyaltyHistoryPage = lazy(() => import("./pages/customers/[customerId]/loyalty-history"));
const CustomerCouponUsageHistoryPage = lazy(() => import("./pages/customers/[customerId]/coupon-usage-history"));
const StaffsPage = lazy(() => import("./pages/staffs"));
const StaffNewPage = lazy(() => import("./pages/staffs/new"));
const StaffDetailPage = lazy(() => import("./pages/staffs/[staffId]"));
const StaffAccountPage = lazy(() => import("./pages/staffs/[staffId]/account"));
const StaffSchedulePage = lazy(() => import("./pages/staffs/[staffId]/schedule"));
const ServicesPage = lazy(() => import("./pages/services"));
const ServiceNewPage = lazy(() => import("./pages/services/new"));
const ServiceDetailPage = lazy(() => import("./pages/services/[serviceId]"));
const ProductsPage = lazy(() => import("./pages/products"));
const ProductNewPage = lazy(() => import("./pages/products/new"));
const ProductDetailPage = lazy(() => import("./pages/products/[productId]"));
const PackagesPage = lazy(() => import("./pages/packages"));
const PackageNewPage = lazy(() => import("./pages/packages/new"));
const PackageDetailPage = lazy(() => import("./pages/packages/[packageId]"));
const CouponsPage = lazy(() => import("./pages/coupons"));
const LoyaltyCampaignsPage = lazy(() => import("./pages/loyalty-campaigns"));
const CouponNewPage = lazy(() => import("./pages/coupons/new"));
const CouponDetailPage = lazy(() => import("./pages/coupons/[couponId]"));
const PaymentsPage = lazy(() => import("./pages/payments"));
const WalletsPage = lazy(() => import("./pages/wallets"));
const ExpensesPage = lazy(() => import("./pages/expenses"));
const ReportsPage = lazy(() => import("./pages/reports"));
const SchedulingPage = lazy(() => import("./pages/scheduling"));
const StorefrontPage = lazy(() => import("./pages/storefront"));
const SubscriptionPage = lazy(() => import("./pages/subscription"));
const SettingsPage = lazy(() => import("./pages/settings"));
const SettingsShopNewPage = lazy(() => import("./pages/settings/shops/new"));
const SettingsShopDetailPage = lazy(() => import("./pages/settings/shops/[shopId]"));
const ProductOrdersPage = lazy(() => import("./pages/product-orders"));
const StaffRequisitionsPage = lazy(() => import("./pages/staff-requisitions"));
const FrontdeskAvailabilityPage = lazy(() => import("./pages/frontdesk-availability"));
const CrmPage = lazy(() => import("./pages/crm"));
const TaxDocumentsPage = lazy(() => import("./pages/tax-documents"));
const SupportPage = lazy(() => import("./pages/support"));
const AiChatPage = lazy(() => import("./pages/ai-chat"));

// Platform Admin pages
const PlatformAdminLoginPage = lazy(() => import("./pages/platform-admin/login"));
const PlatformAdminTenantsPage = lazy(() => import("./pages/platform-admin/tenants"));
const PlatformAdminPlansPage = lazy(() => import("./pages/platform-admin/plans"));
const PlatformAdminWebsiteContentPage = lazy(() => import("./pages/platform-admin/website-content"));
const PlatformAdminSubscriptionsPage = lazy(() => import("./pages/platform-admin/subscriptions"));
const PlatformAdminSubscriptionAddonsPage = lazy(() => import("./pages/platform-admin/subscription-addons"));
const PlatformAdminPaymentConfirmationsPage = lazy(() => import("./pages/platform-admin/payment-confirmations"));
const PlatformAdminWalletsPage = lazy(() => import("./pages/platform-admin/wallets"));
const PlatformAdminPaymentMethodsPage = lazy(() => import("./pages/platform-admin/payment-methods"));
const PlatformAdminSubscriptionPaymentChannelsPage = lazy(() => import("./pages/platform-admin/subscription-payment-channels"));
const PlatformAdminMenuManagementPage = lazy(() => import("./pages/platform-admin/menu-management"));
const PlatformAdminIntegrationRequestsPage = lazy(() => import("./pages/platform-admin/integration-requests"));
const PlatformAdminSupportPage = lazy(() => import("./pages/platform-admin/support"));
const PlatformAdminAccountDeletionRequestsPage = lazy(() => import("./pages/platform-admin/account-deletion-requests"));
const PlatformAdminUsersPage = lazy(() => import("./pages/platform-admin/users"));
const PlatformAdminCustomersPage = lazy(() => import("./pages/platform-admin/customers"));
const PlatformAdminSchedulerSettingsPage = lazy(() => import("./pages/platform-admin/scheduler-settings"));

// Tenant Portal
const DeviceManagePage = lazy(() => import("./pages/device-manage"));
const TenantPortalPage = lazy(() => import("./pages/tenant-portal"));

// Check-in
const CheckInPage = lazy(() => import("./pages/check-in"));

// LINE Pay callback pages
const LinePayConfirmPage = lazy(() => import("./pages/linepay/confirm"));
const LinePayCancelPage = lazy(() => import("./pages/linepay/cancel"));

// Mobile standalone pages
const MobileSubscriptionPage = lazy(() => import("./pages/m/subscription"));

// Wrap component with Suspense
const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType<any>>, transparent?: boolean) => (
  <Suspense fallback={<PageLoader transparent={transparent} />}>
    <Component />
  </Suspense>
);

export const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    errorElement: <RootErrorBoundary />,
    children: [
      // Root redirect
      {
        index: true,
        element: <Navigate to="/login" replace />
      },

      // Auth routes
      {
        path: "login",
        element: withSuspense(LoginPage)
      },
      {
        path: "register",
        element: withSuspense(RegisterPage)
      },
      {
        path: "forgot-password",
        element: withSuspense(ForgotPasswordPage)
      },
      {
        path: "reset-password",
        element: withSuspense(ResetPasswordPage)
      },

      // Protected routes
      {
        element: <ProtectedLayout />,
        children: [
          {
            path: "dashboard",
            element: withSuspense(DashboardPage)
          },
          // Bookings
          {
            path: "bookings",
            element: withSuspense(BookingsPage)
          },
          {
            path: "bookings/new",
            element: withSuspense(BookingNewPage)
          },
          {
            path: "bookings/batch",
            element: withSuspense(BookingBatchPage)
          },
          {
            path: "bookings/personal/:eventId",
            element: withSuspense(PersonalEventEditPage)
          },
          {
            path: "bookings/:bookingId",
            element: withSuspense(BookingDetailPage)
          },
          // Customers
          {
            path: "customers",
            element: withSuspense(CustomersPage)
          },
          {
            path: "customers/new",
            element: withSuspense(CustomerNewPage)
          },
          {
            path: "customers/:customerId",
            element: withSuspense(CustomerDetailPage)
          },
          {
            path: "customers/:customerId/packages",
            element: withSuspense(CustomerPackagesPage)
          },
          {
            path: "customers/:customerId/bookings",
            element: withSuspense(CustomerBookingsPage)
          },
          {
            path: "customers/:customerId/ratings",
            element: withSuspense(CustomerRatingsPage)
          },
          {
            path: "customers/:customerId/coupon-history",
            element: withSuspense(CustomerCouponHistoryPage)
          },
          {
            path: "customers/:customerId/loyalty-history",
            element: withSuspense(CustomerLoyaltyHistoryPage)
          },
          {
            path: "customers/:customerId/coupon-usage-history",
            element: withSuspense(CustomerCouponUsageHistoryPage)
          },
          // Staffs
          {
            path: "staffs",
            element: withSuspense(StaffsPage)
          },
          {
            path: "staffs/new",
            element: withSuspense(StaffNewPage)
          },
          {
            path: "staffs/:staffId",
            element: withSuspense(StaffDetailPage)
          },
          {
            path: "staffs/:staffId/account",
            element: withSuspense(StaffAccountPage)
          },
          {
            path: "staffs/:staffId/schedule",
            element: withSuspense(StaffSchedulePage)
          },
          // Services
          {
            path: "services",
            element: withSuspense(ServicesPage)
          },
          {
            path: "services/new",
            element: withSuspense(ServiceNewPage)
          },
          {
            path: "services/:serviceId",
            element: withSuspense(ServiceDetailPage)
          },
          // Products
          {
            path: "products",
            element: withSuspense(ProductsPage)
          },
          {
            path: "products/new",
            element: withSuspense(ProductNewPage)
          },
          {
            path: "products/:productId",
            element: withSuspense(ProductDetailPage)
          },
          // Packages
          {
            path: "packages",
            element: withSuspense(PackagesPage)
          },
          {
            path: "packages/new",
            element: withSuspense(PackageNewPage)
          },
          {
            path: "packages/:packageId",
            element: withSuspense(PackageDetailPage)
          },
          // Coupons
          {
            path: "coupons",
            element: withSuspense(CouponsPage)
          },
          {
            path: "coupons/new",
            element: withSuspense(CouponNewPage)
          },
          {
            path: "loyalty-campaigns",
            element: withSuspense(LoyaltyCampaignsPage)
          },
          {
            path: "coupons/:couponId",
            element: withSuspense(CouponDetailPage)
          },
          // Others
          {
            path: "payments",
            element: withSuspense(PaymentsPage)
          },
          {
            path: "wallets",
            element: withSuspense(WalletsPage)
          },
          {
            path: "expenses",
            element: withSuspense(ExpensesPage)
          },
          {
            path: "reports",
            element: withSuspense(ReportsPage)
          },
          {
            path: "scheduling",
            element: withSuspense(SchedulingPage)
          },
          {
            path: "storefront",
            element: withSuspense(StorefrontPage)
          },
          {
            path: "subscription",
            element: withSuspense(SubscriptionPage)
          },
          {
            path: "product-orders",
            element: withSuspense(ProductOrdersPage)
          },
          {
            path: "staff-requisitions",
            element: withSuspense(StaffRequisitionsPage)
          },
          {
            path: "frontdesk-availability",
            element: withSuspense(FrontdeskAvailabilityPage)
          },
          // CRM
          {
            path: "crm",
            element: withSuspense(CrmPage)
          },
          // Tax Documents
          {
            path: "tax-documents",
            element: withSuspense(TaxDocumentsPage)
          },
          // Settings
          {
            path: "settings",
            element: withSuspense(SettingsPage)
          },
          {
            path: "settings/shops/new",
            element: withSuspense(SettingsShopNewPage)
          },
          {
            path: "settings/shops/:shopId",
            element: withSuspense(SettingsShopDetailPage)
          },
          // Device management (nav module)
          {
            path: "device-manage",
            element: withSuspense(DeviceManagePage)
          },
          // Support
          {
            path: "support",
            element: withSuspense(SupportPage)
          },
          // AI Chat
          {
            path: "ai-chat",
            element: withSuspense(AiChatPage)
          }
        ]
      },

      // Platform Admin routes
      {
        path: "platform-admin",
        children: [
          {
            index: true,
            element: <Navigate to="/platform-admin/login" replace />
          },
          {
            path: "login",
            element: withSuspense(PlatformAdminLoginPage)
          },
          {
            element: <PlatformAdminProtectedLayout />,
            children: [
              {
                path: "tenants",
                element: withSuspense(PlatformAdminTenantsPage)
              },
              {
                path: "plans",
                element: withSuspense(PlatformAdminPlansPage)
              },
              {
                path: "website-content",
                element: withSuspense(PlatformAdminWebsiteContentPage)
              },
              {
                path: "subscriptions",
                element: withSuspense(PlatformAdminSubscriptionsPage)
              },
              {
                path: "subscription-addons",
                element: withSuspense(PlatformAdminSubscriptionAddonsPage)
              },
              {
                path: "payment-confirmations",
                element: withSuspense(PlatformAdminPaymentConfirmationsPage)
              },
              {
                path: "wallets",
                element: withSuspense(PlatformAdminWalletsPage)
              },
              {
                path: "payment-methods",
                element: withSuspense(PlatformAdminPaymentMethodsPage)
              },
              {
                path: "subscription-payment-channels",
                element: withSuspense(PlatformAdminSubscriptionPaymentChannelsPage)
              },
              {
                path: "menu-management",
                element: withSuspense(PlatformAdminMenuManagementPage)
              },
              {
                path: "integration-requests",
                element: withSuspense(PlatformAdminIntegrationRequestsPage)
              },
              {
                path: "support",
                element: withSuspense(PlatformAdminSupportPage)
              },
              {
                path: "account-deletion-requests",
                element: withSuspense(PlatformAdminAccountDeletionRequestsPage)
              },
              {
                path: "users",
                element: withSuspense(PlatformAdminUsersPage)
              },
              {
                path: "customers",
                element: withSuspense(PlatformAdminCustomersPage)
              },
              {
                path: "scheduler-settings",
                element: withSuspense(PlatformAdminSchedulerSettingsPage)
              }
            ]
          }
        ]
      },

      // Tenant Portal
      {
        path: "tenant-portal",
        element: withSuspense(TenantPortalPage, true)
      },
      {
        path: "tenant-portal/:slug",
        element: withSuspense(TenantPortalPage, true)
      },

      // LINE Pay callback
      {
        path: "linepay/confirm",
        element: withSuspense(LinePayConfirmPage)
      },
      {
        path: "linepay/cancel",
        element: withSuspense(LinePayCancelPage)
      },

      // QR Code Check-in
      {
        path: "check-in/:bookingId",
        element: withSuspense(CheckInPage)
      },

      // Mobile standalone pages (token-based auth, no layout)
      {
        path: "m/subscription",
        element: withSuspense(MobileSubscriptionPage)
      }
    ]
  }
]);
